// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * @fileoverview Zod schema for the git_comments VCA collection.
 *
 * Issue, PR, commit, and review thread comments from any connected provider.
 * Stored as a unified collection; `parentType` discriminates the context.
 *
 * Named `git_comments` (prefix `gcm_`) to avoid collision with the existing
 * generic `comment` entity (prefix `cmnt`) used by process flows.
 *
 * Identity tuple: (gitConnectionId, repoId, commentId, parentType)
 * ID prefix: gcm_
 */

import { z } from 'zod'

export const git_commentsSchema = z.object({
  id: z.string().max(100),
  orgId: z.string().max(100),
  workspaceId: z.string().max(100).optional(),

  // ── Git connection reference ──────────────────────────────────────────
  gitConnectionId: z.string().max(100),
  codebaseId: z.string().max(100).optional(),

  // ── Identity tuple ────────────────────────────────────────────────────
  repoId: z.string().max(200),
  /** Provider-assigned comment identifier. */
  commentId: z.string().max(200),
  /** The kind of entity this comment belongs to. */
  parentType: z.enum(['issue', 'pr', 'commit', 'review']),

  // ── Parent reference ──────────────────────────────────────────────────
  /**
   * Provider-side identifier of the parent entity.
   * For 'issue' or 'pr': the issue/PR number as a string.
   * For 'commit': the commit SHA.
   * For 'review': the review identifier.
   */
  parentId: z.string().max(200),

  // ── Comment content ───────────────────────────────────────────────────
  body: z.string(),
  authorLogin: z.string().max(200),

  // ── Timestamps ───────────────────────────────────────────────────────
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),

  // ── Provider-specific extras ──────────────────────────────────────────
  providerData: z.record(z.string(), z.unknown()).optional(),
})

export type Git_commentsInput = z.infer<typeof git_commentsSchema>
